/* ---------- React ---------- */
import React, { createRef } from 'react';
import PropTypes from 'prop-types';

/* ---------- Components ---------- */
import EnhancedTable from './EnhancedTable';
import { ComponentDictionary } from '../components';

/* ---------- Utils ---------- */
import defaultGridProps from '../utils/defaultProps';
import helperUtil from '../utils/helper.util';
import styles from '../styles/display.styles';
import { css } from '@emotion/core';
import _ from 'lodash';

const DISPLAY_NAME = 'DisplayTable';
const { noop } = helperUtil;

const DisplayDecorator = BaseGrid =>
  class DisplayTable extends EnhancedTable {
    static displayName = DISPLAY_NAME;

    static propTypes = {
      onCellChange: PropTypes.func,
      updateDisplayedData: PropTypes.func,
      onPageChange: PropTypes.func,
      onSort: PropTypes.func,
      updateFilteredData: PropTypes.func,
      selected: PropTypes.bool
    };

    static defaultProps = {
      ...defaultGridProps,
      updateDisplayedData: noop,
      onCellChange: noop,
      onPageChange: noop,
      onSelectRow: noop,
      onSort: noop,
      updateFilteredData: noop,
      filters: [],
      tableId: 'displayTableId',
      variant: 'display'
    };

    constructor(props) {
      super({ ...props, childDisplayName: DISPLAY_NAME });
      this.tableContainerRef = createRef();
    }

    getCellWidthStyle = headerIdx => {
      const isRowAction = this.isRowActionIdx(headerIdx);
      let width;

      if (!isRowAction) {
        width = this.getResolvedHeaders()?.[headerIdx]?.width || 201;
      }

      return css`
        ${!isRowAction ? `min-width: ${width}px;` : ''}
        ${!isRowAction ? `max-width: ${width}px;` : ''}
      `;
    };

    getTheadProps = () => ({});

    getThProps = headerIdx => ({ css: this.getCellWidthStyle(headerIdx) });

    getTdProps = (rowIdx, cellIdx) => ({
      css: this.getCellWidthStyle(cellIdx)
    });

    getTrProps = () => ({
      className: 'gridRow',
      css: styles.trWrapper
    });

    renderCell = (cell, colIdx, rowIdx, onChange) => {
      cell = cell ?? {};
      const { components: COMPONENTS, props, tableContainerRef } = this;
      const { dataTest: dataTestFromProps, tableId } = props;
      const { type, value } = cell;
      const Component = ComponentDictionary[type];
      let { dataTest } = cell;

      dataTest =
        dataTest ?? `cellDataTest-${dataTestFromProps}-${rowIdx}-${colIdx}`;

      dataTest = `${dataTest}-${type}-${colIdx}`;

      const baseProps = {
        cell,
        colIdx,
        dataTest,
        rowIdx,
        tableId,
        value,
        ...(type !== COMPONENTS.STATUS_BADGE ? cell : {})
      };

      if (type === COMPONENTS.FILTER_DROPDOWN && !cell?.isSticky) {
        const hasStickyColsClassName =
          !!tableContainerRef.current?.querySelector?.('.sticky-columns');

        if (hasStickyColsClassName) {
          const { inputProps, onBlur, onSelect } = baseProps;
          const { onInputClick, onInputFocus } = inputProps || {};
          const getTh = event => event?.target?.closest?.('th');
          baseProps.doNotBlurOnEsc = true;

          const isFilterFocused = th =>
            th?.classList?.contains?.('focused-filter-dropdown-cell');

          const withAddClassName = cb => event => {
            typeof cb === 'function' && cb(event);
            const th = getTh(event);

            if (th && !isFilterFocused(th)) {
              th.classList.add('focused-filter-dropdown-cell');
            }
          };

          baseProps.onSelect = (event, ...rest) => {
            typeof onSelect === 'function' && onSelect(event, ...rest);
            const th = document.querySelector('.focused-filter-dropdown-cell');

            if (cell?.variant !== 'multi' && th) {
              th.classList.remove('focused-filter-dropdown-cell');
            }
          };

          baseProps.onBlur = (event, ...rest) => {
            typeof onBlur === 'function' && onBlur(event, ...rest);
            const th = getTh(event);

            if (isFilterFocused(th)) {
              th.classList.remove('focused-filter-dropdown-cell');
            }
          };

          baseProps.inputProps = {
            ...(inputProps || {}),
            onInputClick: withAddClassName(onInputClick),
            onInputFocus: withAddClassName(onInputFocus),
            placeholder: inputProps?.placeholder ?? cell.placeholder ?? ''
          };
        }
      }

      switch (type) {
        case COMPONENTS.ACTIVE_FILTER:
        case COMPONENTS.BUTTON:
        case COMPONENTS.ICON:
        case COMPONENTS.LINK:
          return <Component {...baseProps} onClick={onChange} />;

        case COMPONENTS.CHECKBOX:
          return <Component {...baseProps} onCheck={onChange} />;

        case COMPONENTS.FILTER_DROPDOWN:
        case COMPONENTS.FILTER_INPUT:
        case COMPONENTS.FILTER_INPUT_TEXT:
        case COMPONENTS.FILTER_INPUT_NUMBER:
          return <Component {...baseProps} onCellChange={onChange} />;

        case COMPONENTS.STATIC_FIELD:
          return <Component label={value} {...cell} />;

        case COMPONENTS.STATUS_BADGE:
          return <Component {...baseProps} />;

        default:
          return <div />;
      }
    };

    getProps = () => {
      const {
        getCommonProps,
        getTdProps,
        getThProps,
        getTheadProps,
        getTrProps,
        renderCell,
        tableContainerRef: ref,
        types
      } = this;

      const { renderTableFilter, ...props } = getCommonProps();

      return {
        ...props,
        getTdProps,
        getThProps,
        getTheadProps,
        getTrProps,
        ref,
        renderCell,
        renderTableFilter: renderTableFilter(renderCell),
        styles,
        variant: types.DISPLAY
      };
    };

    render = () => {
      return <BaseGrid {...this.getProps()} />;
    };
  };

export default DisplayDecorator;
